import type { MetricAssignmentType } from '~/types/types'
import { AnnualAggregationMethod, MetricValueKind } from '~/types/types'

type AggregationPeriodView = {
  period: string
  rawActual?: number | null
  rawTarget?: number | null
  rawPerformancePercent?: number | null
  accumulatedActual?: number | null
  accumulatedTarget?: number | null
  accumulatedPerformancePercent?: number | null
  isMostRecentActivePeriod?: boolean
}

type AggregationView = {
  calculationType?: string | null
  summary?: {
    performancePercent?: number | null
  } | null
  periods?: AggregationPeriodView[] | null
}

type LatestPeriodPerformanceDetail = {
  performancePercent: number | null
  period: string | null
}

export function shouldRenderMetricAssignment(
  assignment?: MetricAssignmentType | null,
): boolean {
  if (!assignment) return false
  if (assignment.completionStatus?.hasTarget) return true
  return (assignment.metricValues ?? []).some(
    (metricValue) =>
      metricValue?.target !== null && metricValue?.target !== undefined,
  )
}

function toNumericValue(value: unknown): number | null {
  if (value === null || value === undefined) return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

function isComparableValueType(valueType?: MetricValueKind | string | null): boolean {
  if (!valueType) return false
  const normalized = String(valueType).toUpperCase()
  return normalized === 'INT' || normalized === 'FLOAT' || normalized === 'BINARY'
}

function getEffectiveCalculationType(
  assignment: MetricAssignmentType,
  aggregationView: AggregationView | null,
): AnnualAggregationMethod | null {
  const apiCalcType = aggregationView?.calculationType
  if (apiCalcType === 'sum') return AnnualAggregationMethod.Sum
  if (apiCalcType === 'average') return AnnualAggregationMethod.Avg
  if (apiCalcType === 'most_recent_value') return AnnualAggregationMethod.MostRecent

  const assignmentType = assignment.customTargetCalculationType
  if (assignmentType) return assignmentType

  const legacy = assignment.completionStatus?.targetParameters?.calculationType
  const legacyNormalized = legacy ? String(legacy).toLowerCase() : null
  if (legacyNormalized === 'sum') return AnnualAggregationMethod.Sum
  if (legacyNormalized === 'average') return AnnualAggregationMethod.Avg
  if (legacyNormalized === 'most_recent_value') return AnnualAggregationMethod.MostRecent
  return null
}

function normalizePeriod(period: string | number | null | undefined): string | null {
  if (period === null || period === undefined || period === '') return null
  return String(period)
}

function findLatestPeriodWithValidPercentage(
  rowsByPeriod: Record<string, { percentageAchieved: number | null }>,
  periodColumns: string[],
): string | null {
  for (let i = periodColumns.length - 1; i >= 0; i -= 1) {
    const period = periodColumns[i]
    const percentage = rowsByPeriod[period]?.percentageAchieved ?? null
    if (percentage !== null && percentage !== undefined) return period
  }
  return null
}

/**
 * Latest-period performance % for on-track chip and card header, read from the
 * server-computed aggregation view (`completionStatus.aggregationView`) — the
 * single source of truth. The backend always emits a view when data exists, so
 * there is no client-side recompute.
 * Sum/Avg: accumulated performance on the latest reported period.
 * Most recent: raw performance on the active most-recent period.
 */
export function getLatestPeriodPerformanceDetail(
  assignment: MetricAssignmentType,
): LatestPeriodPerformanceDetail {
  const valueType = assignment.metric?.valueType
  if (!isComparableValueType(valueType)) {
    return { performancePercent: null, period: null }
  }

  const aggregationView =
    ((assignment.completionStatus as { aggregationView?: AggregationView })?.aggregationView ||
      null) as AggregationView | null
  if (!aggregationView?.periods?.length) {
    return { performancePercent: null, period: null }
  }

  const calculationType = getEffectiveCalculationType(assignment, aggregationView)
  const isMostRecent = calculationType === AnnualAggregationMethod.MostRecent

  if (isMostRecent) {
    const activeRow =
      aggregationView.periods.find((row) => row.isMostRecentActivePeriod) ||
      [...aggregationView.periods]
        .reverse()
        .find((row) => toNumericValue(row.rawPerformancePercent) !== null)
    return {
      performancePercent: toNumericValue(activeRow?.rawPerformancePercent),
      period: normalizePeriod(activeRow?.period),
    }
  }

  const periodColumns = aggregationView.periods.map((row) => String(row.period))
  const rowsByPeriod = Object.fromEntries(
    aggregationView.periods.map((row) => [
      String(row.period),
      { percentageAchieved: toNumericValue(row.accumulatedPerformancePercent) },
    ]),
  )
  const latestPeriod = findLatestPeriodWithValidPercentage(rowsByPeriod, periodColumns)
  if (!latestPeriod) return { performancePercent: null, period: null }
  const row = aggregationView.periods.find(
    (entry) => normalizePeriod(entry.period) === latestPeriod,
  )
  return {
    performancePercent: toNumericValue(row?.accumulatedPerformancePercent),
    period: latestPeriod,
  }
}

export function getLatestPeriodPerformancePercent(
  assignment: MetricAssignmentType,
): number | null {
  return getLatestPeriodPerformanceDetail(assignment).performancePercent
}

export function isKpiOnTrack(assignment: MetricAssignmentType): boolean | null {
  const performance = getLatestPeriodPerformancePercent(assignment)
  if (performance === null) return null
  return performance >= 100
}

/** DB may store 0.1 (fraction) or 10 (percent); always returns 0–100. */
export function formatCustomTargetWeightPercent(
  raw: number | null | undefined,
): number | null {
  if (raw === null || raw === undefined) return null
  const numeric = Number(raw)
  if (!Number.isFinite(numeric)) return null
  const percent = numeric <= 1 ? numeric * 100 : numeric
  return Math.round(percent)
}

export function normalizeCustomTargetWeight(
  raw: number | null | undefined,
): number | null {
  if (raw === null || raw === undefined) return null
  const numeric = Number(raw)
  if (!Number.isFinite(numeric)) return null
  return numeric <= 1 ? numeric : numeric / 100
}

export function hasCarryRemarks(value: unknown): boolean {
  if (value === null || value === undefined) return false
  return String(value).trim().length > 0
}

export function shouldShowCustomTargetWeight(
  raw: number | null | undefined,
  multipleKpis: boolean,
): boolean {
  if (!multipleKpis) return false
  const percent = formatCustomTargetWeightPercent(raw)
  return percent !== null && percent !== 100
}

export function countKpisOnTrack(assignments: MetricAssignmentType[]): {
  onTrack: number
  total: number
} {
  const rendered = assignments.filter(shouldRenderMetricAssignment)
  const statusDetails = rendered.map((assignment) => {
    const performance = getLatestPeriodPerformancePercent(assignment)
    const onTrack = performance === null ? null : performance >= 100
    return {
      assignmentId: assignment.id,
      metricId: assignment.metric?.id,
      metricName: assignment.metricNameOverride || assignment.metric?.name,
      valueType: assignment.metric?.valueType,
      performance,
      onTrack,
    }
  })
  const withStatus = statusDetails
    .map((entry) => entry.onTrack)
    .filter((status): status is boolean => status !== null)

  const result = {
    onTrack: withStatus.filter(Boolean).length,
    total: withStatus.length,
  }

  return result
}

export function getImpactCarryWeightedSummary(assignments: MetricAssignmentType[]): {
  weightedPerformancePercent: number | null
  latestReportedPeriod: string | null
  missingWeightCount: number
  totalKpis: number
} {
  const rendered = assignments.filter(shouldRenderMetricAssignment)
  if (!rendered.length) {
    return {
      weightedPerformancePercent: null,
      latestReportedPeriod: null,
      missingWeightCount: 0,
      totalKpis: 0,
    }
  }

  let weightedSum = 0
  let hasWeightedContribution = false
  let latestReportedPeriod: string | null = null
  let missingWeightCount = 0

  rendered.forEach((assignment) => {
    const detail = getLatestPeriodPerformanceDetail(assignment)
    if (detail.period !== null) {
      if (
        latestReportedPeriod === null ||
        Number(detail.period) > Number(latestReportedPeriod)
      ) {
        latestReportedPeriod = detail.period
      }
    }
    const normalizedWeight = normalizeCustomTargetWeight(
      assignment.customTargetWeight as number | null | undefined,
    )
    if (normalizedWeight === null) {
      missingWeightCount += 1
      return
    }
    if (detail.performancePercent === null) return
    weightedSum += normalizedWeight * detail.performancePercent
    hasWeightedContribution = true
  })

  return {
    weightedPerformancePercent: hasWeightedContribution && Number.isFinite(weightedSum)
      ? Math.round(weightedSum)
      : null,
    latestReportedPeriod,
    missingWeightCount,
    totalKpis: rendered.length,
  }
}
